import client from "@/api/client"
import IconButton from "@/components/IconButton"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useAuth } from "@/providers/authContext"
import { useEffect, useState } from "react"
import { queryClient } from "@/providers/QueryProvider"
import Modal from "@/components/Modal"

export const ApiKey = () => {
  const { bearerToken } = useAuth()
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; id: string; name: string }>({
    open: false,
    id: "",
    name: ""
  })
  const [createModal, setCreateModal] = useState<{ open: boolean; name: string }>({
    open: false,
    name: ""
  })
  const [newApiKey, setNewApiKey] = useState<{ key: string; name: string } | null>(null)
  const [copiedToClipboard, setCopiedToClipboard] = useState(false)
  
  const { data: apiKeys } = useQuery({
    queryKey: ["api-key"],
    queryFn: async () => {
      const res = await client.api.apiKeyControllerGetApiKeys()
      return res.data.apiKeys
    },
    enabled: !!bearerToken,
  })
  
  const { mutate: deleteApiKey } = useMutation({
    mutationFn: async (id: string) => {
      const res = await client.api.apiKeyControllerRevokeApiKey(id)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-key"] })
    },
  })
  
  const { mutate: createApiKey, isPending: isCreating } = useMutation({
    mutationFn: async (name: string) => {
      const res = await client.api.apiKeyControllerCreateApiKey({ name })
      return res.data
    },
    onSuccess: (data) => {
      setNewApiKey({
        key: data.apiKey,
        name: data.name || "Unnamed API Key"
      })
      queryClient.invalidateQueries({ queryKey: ["api-key"] })
    },
  })

  const handleDeleteApiKey = (id: string) => {
    deleteApiKey(id)
    setDeleteConfirmation({ open: false, id: "", name: "" })
  }
  
  const showDeleteConfirmation = (id: string, name: string) => {
    setDeleteConfirmation({
      open: true,
      id,
      name
    })
  }
  
  const handleCreateApiKey = () => {
    if (!createModal.name.trim()) return
    createApiKey(createModal.name)
    setCreateModal({ open: false, name: "" })
  }

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(newApiKey?.key || "")
    setCopiedToClipboard(true)
    
    // Reset after 3 seconds
    setTimeout(() => {
      setCopiedToClipboard(false)
    }, 3000)
  }

  useEffect(() => {
    // Reset copied state when modal opens/closes
    if (!newApiKey) {
      setCopiedToClipboard(false)
    }
  }, [newApiKey])

  return (
    <div className="flex flex-col">
      <p className="text-[#FFF8EE] font-semibold mb-3">API Key</p>
      <p className="text-atherGray-300 text-sm mb-3">
        API keys are used to authenticate your requests to the API. You can create multiple API keys for different
        purposes.
      </p>
      <IconButton
        colorScheme="secondary"
        className="p-2 rounded-lg w-fit mb-4"
        onClick={() => setCreateModal({ open: true, name: "" })}
      >
        <p className="text-[#fff8ee] text-sm font-semibold">Create API Key</p>
      </IconButton>

      {/* API Keys Table */}
      <div className="w-full overflow-x-auto rounded-lg border border-atherGray-800">
        <table className="w-full min-w-full table-auto">
          <thead className="bg-atherGray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#FFF8EE]">Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-[#FFF8EE]">Created</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-[#FFF8EE]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {apiKeys && apiKeys.length > 0 ? (
              apiKeys.map(apiKey => (
                <tr key={apiKey.id} className="border-t border-atherGray-800 hover:bg-atherGray-900/50">
                  <td className="px-4 py-3 text-sm text-[#FFF8EE]">{apiKey.name}</td>
                  <td className="px-4 py-3 text-sm text-[#FFF8EE]">
                    {new Date(apiKey.createdAt).toLocaleDateString('en-GB', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric' 
                    })} {new Date(apiKey.createdAt).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </td>
                  <td className="px-4 py-3 flex justify-end">
                    <IconButton 
                      colorScheme="secondary" 
                      className="p-2 rounded-lg"
                      onClick={() => showDeleteConfirmation(apiKey.id, apiKey.name || "Unnamed API Key")}
                    >
                      <p className="text-[#fff8ee] text-sm font-semibold">Delete</p>
                    </IconButton>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-sm text-[#FFF8EE]">
                  No API keys found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, id: "", name: "" })}
        title="Delete API Key"
        className="max-w-md"
      >
        <div className="flex flex-col space-y-4">
          <p className="text-sm text-[#FFF8EE]">
            Are you sure you want to delete the API key <span className="font-semibold">{deleteConfirmation.name}</span>?
            This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3 mt-4">
            <IconButton
              colorScheme="secondary"
              className="p-2 rounded-lg"
              onClick={() => setDeleteConfirmation({ open: false, id: "", name: "" })}
            >
              <p className="text-[#fff8ee] text-sm font-semibold">Cancel</p>
            </IconButton>
            <IconButton
              colorScheme="primary"
              className="p-2 rounded-lg bg-red-600 hover:bg-red-700"
              onClick={() => handleDeleteApiKey(deleteConfirmation.id)}
            >
              <p className="text-[#fff8ee] text-sm font-semibold">Delete</p>
            </IconButton>
          </div>
        </div>
      </Modal>
      
      {/* Create API Key Modal */}
      <Modal
        isOpen={createModal.open}
        onClose={() => setCreateModal({ open: false, name: "" })}
        title="Create API Key"
        className="max-w-md"
      >
        <div className="flex flex-col space-y-4">
          <div>
            <label htmlFor="apiKeyName" className="block text-sm font-medium text-[#FFF8EE] mb-1">
              API Key Name
            </label>
            <input
              id="apiKeyName"
              type="text"
              value={createModal.name}
              onChange={(e) => setCreateModal(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter a name for your API key"
              className="w-full px-3 py-2 bg-atherGray-800 border border-atherGray-700 rounded-md text-[#FFF8EE] focus:outline-none focus:ring-2 focus:ring-atherPurple-500"
            />
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <IconButton
              colorScheme="secondary"
              className="p-2 rounded-lg"
              onClick={() => setCreateModal({ open: false, name: "" })}
            >
              <p className="text-[#fff8ee] text-sm font-semibold">Cancel</p>
            </IconButton>
            <IconButton
              colorScheme="primary"
              className="p-2 rounded-lg bg-atherPurple-600 hover:bg-atherPurple-700"
              onClick={handleCreateApiKey}
              disabled={!createModal.name.trim() || isCreating}
            >
              <p className="text-[#fff8ee] text-sm font-semibold">{isCreating ? "Creating..." : "Create"}</p>
            </IconButton>
          </div>
        </div>
      </Modal>
      
      {/* API Key Created Modal */}
      {newApiKey && (
        <Modal
          isOpen={!!newApiKey}
          onClose={() => setNewApiKey(null)}
          title="API Key Created"
          className="max-w-md"
        >
          <div className="flex flex-col space-y-4">
            <p className="text-sm text-[#FFF8EE]">
              Your new API key <span className="font-semibold">{newApiKey.name}</span> has been created. 
              Please copy this key now as you won&apos;t be able to see it again.
            </p>
            <div className="bg-atherGray-800 p-3 rounded-md">
              <p className="text-xs text-[#FFF8EE] break-all font-mono">{newApiKey.key}</p>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <IconButton
                colorScheme={copiedToClipboard ? "primary" : "secondary"}
                className={`p-2 rounded-lg ${copiedToClipboard ? "bg-green-600 hover:bg-green-700" : ""}`}
                onClick={handleCopyToClipboard}
                disabled={copiedToClipboard}
              >
                <p className="text-[#fff8ee] text-sm font-semibold">
                  {copiedToClipboard ? "Copied!" : "Copy to Clipboard"}
                </p>
              </IconButton>
              <IconButton
                colorScheme="primary"
                className="p-2 rounded-lg bg-atherPurple-600 hover:bg-atherPurple-700"
                onClick={() => setNewApiKey(null)}
              >
                <p className="text-[#fff8ee] text-sm font-semibold">Done</p>
              </IconButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
